import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { isNil, kebabCase } from 'lodash-es';
import { log } from './log';

export type TelemetryConfig = {
  instrumentationKey: string | null;
};

export class Telemetry {
  private applicationInsights: ApplicationInsights;

  constructor(instrumentationKey: string) {
    this.applicationInsights = new ApplicationInsights({
      config: {
        instrumentationKey,
        enableAutoRouteTracking: true,
        excludeRequestFromAutoTrackingPatterns: [
          '/login/',
          '/version.json',
        ],
      },
    });
    this.applicationInsights.loadAppInsights();
    this.applicationInsights.trackPageView({
      properties: {
        ScreenResolution: getScreenResolution(),
        IsTouchDevice: isTouchDevice(),
      },
    });

    this.setupOperationNameLogging();
  }

  trackEvent<T extends Record<string, unknown>>(name: string, properties: T) {
    this.applicationInsights.trackEvent({
      name,
      properties,
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  trackException(exception: Error, properties?: Record<string, any>) {
    this.applicationInsights.trackException({
      exception,
      properties,
    });
  }

  setClientVersion(clientVersion: string) {
    log.trace(`Setting application version to ${clientVersion}.`);
    this.applicationInsights.context.application.ver = clientVersion;
  }

  setAuthenticatedUser(email: string, organisation: string) {
    const sanitizedOrganisation = kebabCase(organisation);
    log.trace(`Setting authenticated user to ${email} (${sanitizedOrganisation}).`);
    this.applicationInsights.setAuthenticatedUserContext(email, sanitizedOrganisation, true);
  }

  unsetAuthenticatedUser() {
    log.trace('Unsetting authenticated user.');
    this.applicationInsights.clearAuthenticatedUserContext();
  }

  destroy() {
    log.trace('Unloading telemetry.');
    this.applicationInsights.unload();
  }

  private setupOperationNameLogging() {
    this.applicationInsights.addDependencyListener(details => {
      try {
        const requestBody = JSON.parse(details.init?.body as string);
        details.context = details.context ?? {};
        details.context['operationName'] = requestBody.operationName;
        details.context['variables'] = requestBody.variables;
      } catch (_) {
        /* no JSON response body, no GraphQL operation */
      }
    });

    this.applicationInsights.addDependencyInitializer(details => {
      const operationName = details.context?.['operationName'];
      const variables = details.context?.['variables'];
      if (!isNil(operationName)) {
        details.item.properties = details.item.properties ?? {};
        details.item.properties['OperationName'] = operationName;
        details.item.properties['Variables'] = variables;
      }
    });
  }
}

const getScreenResolution = () => ({
  w: window.screen.width,
  h: window.screen.height,
  d: window.screen.pixelDepth,
});

const isTouchDevice = () => {
  return (
    ('ontouchstart' in window) ||
    (navigator.maxTouchPoints > 0) ||
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    (navigator.msMaxTouchPoints > 0));
};
